import React from 'react';
import PropTypes from 'prop-types';

import EmojiPicker    from 'app/components/common/emoji-picker';
import Icon           from 'app/components/common/icon';
import Popper         from 'app/components/common/popper';
import Menu           from 'app/components/common/rich-text/wysiwyg-menu';
import StandardInput  from 'app/components/common/standard-input';
import Pm             from 'app/helpers/rich-text/prose-mirror';
import utils          from 'app/helpers/utils';

const textTypeAbbrMap = {
  paragraph: 'Text',
  heading1: 'H1',
  heading2: 'H2',
  heading3: 'H3',
  heading4: 'H4',
  code_block: 'Code',
};

const noop = () => {};
const selToStr = (pmSelection) => {
  if (!pmSelection) return '0-0';
  return `${pmSelection.from}-${pmSelection.to}`;
};
const isSelStrEmpty = (selStr) => {
  const [from, to] = selStr.split('-');
  return from === to;
};
const getSelectionChangeFacts = (prevSelStr, newSelStr) => {
  const wasEmpty = isSelStrEmpty(prevSelStr);
  const isEmpty  = isSelStrEmpty(newSelStr);
  const becameEmpty = !wasEmpty &&  isEmpty;
  const becameRange =  wasEmpty && !isEmpty;
  const stayedEmpty =  wasEmpty &&  isEmpty;
  const stayedRange = !wasEmpty && !isEmpty;
  return {wasEmpty, isEmpty, becameEmpty, becameRange, stayedEmpty, stayedRange};
};

class RteWysiwyg extends React.PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      linkHref: null,
      menuState: Pm.defaultMenuState,
      hasContent: (props.initialMd || '').trim().length,
      selStr: '0-0',
      isCreatingLink: false,
      inlineMenu: null,
      hasFocus: false,
    };

    this.refMain = React.createRef();
    this.refEditorCon = React.createRef();
    this.refPopper = React.createRef();
    this.refMenu = React.createRef();
    this.refHrefInput = React.createRef();
    this.refInlineMenu = React.createRef();

    this.onPmUpdate = this.onPmUpdate.bind(this);

    this.onClickLink = this.onClickLink.bind(this);
    this.onChangeLinkHref = this.onChangeLinkHref.bind(this);
    this.onKeyPressLinkHref = this.onKeyPressLinkHref.bind(this);
    this.onClickLinkAccept = this.onClickLinkAccept.bind(this);
    this.onClickLinkRemove = this.onClickLinkRemove.bind(this);
    this.onClickEmojis = this.onClickEmojis.bind(this);
    this.onSelectEmoji = this.onSelectEmoji.bind(this);

    this.preventDefault = this.preventDefault.bind(this);
    this.onClickListifyBullet = this.onClickListifyBullet.bind(this);
    this.onClickListifyNumber = this.onClickListifyNumber.bind(this);
    this.onClickListSink = this.onClickListSink.bind(this);
    this.onClickListLift = this.onClickListLift.bind(this);
    this.onClickQuote = this.onClickQuote.bind(this);
    this.onClickLift = this.onClickLift.bind(this);
    this.onClickInsertNewLine = this.onClickInsertNewLine.bind(this);
    this.onClickInsertHr = this.onClickInsertHr.bind(this);

    this.onClickTextTypeH1 = this.onClickTextTypeH1.bind(this);
    this.onClickTextTypeH2 = this.onClickTextTypeH2.bind(this);
    this.onClickTextTypeH3 = this.onClickTextTypeH3.bind(this);
    this.onClickTextTypeH4 = this.onClickTextTypeH4.bind(this);
    this.onClickTextTypeText = this.onClickTextTypeText.bind(this);
    this.onClickTextTypeCode = this.onClickTextTypeCode.bind(this);
    this.onSelectTextType = this.onSelectTextType.bind(this);

    this.onClickMarkStrong = this.onClickMarkStrong.bind(this);
    this.onClickMarkEm = this.onClickMarkEm.bind(this);
    this.onClickMarkCode = this.onClickMarkCode.bind(this);
    this.onClickMark = this.onClickMark.bind(this);

    this.renderInlineMenuLink = this.renderInlineMenuLink.bind(this);
    this.renderInlineMenuEmojis = this.renderInlineMenuEmojis.bind(this);

    this.onBlurEditable = this.onBlurEditable.bind(this);
    this.onFocusEditable = this.onFocusEditable.bind(this);
    this.onClickDocument = this.onClickDocument.bind(this);
  }

  componentDidMount() {
    const elEditorCon = this.refEditorCon.current;
    this._pmView = Pm.makeView(elEditorCon, this.props.initialMd, this.onPmUpdate);
    this.elEditable.addEventListener('focus', this.onFocusEditable);
    this.elEditable.addEventListener('blur',  this.onBlurEditable);
    document.addEventListener('click', this.onClickDocument);
  }

  componentDidUpdate(prevProps, prevState) {
    const didSelChange = prevState.selStr !== this.state.selStr;
    if (didSelChange) this.handleSelectionChange(prevState.selStr, this.state.selStr);
  }

  componentWillUnmount() {
    this.elEditable.removeEventListener('focus', this.onFocusEditable);
    this.elEditable.removeEventListener('blur',  this.onBlurEditable);
    document.removeEventListener('click', this.onClickDocument);
  }

  onClickDocument(event) {
    const containsTarget = this.getContainsEl(event.target);
    if (!containsTarget) {
      this.onClickOutside();
    }
  }

  onClickOutside() {
    this.setState({inlineMenu: null, hasFocus: false});
  }

  handleSelectionChange(prevSelStr, newSelStr) {
    this.setState({inlineMenu: null});
    // const {isEmpty, becameRange, stayedRange} = getSelectionChangeFacts(prevSelStr, newSelStr);
  }

  onFocusEditable(event) {
    this.setState((prevState) => {
      if (prevState.hasFocus) return prevState;
      return {hasFocus: true};
    });
  }
  onBlurEditable(event) {
    // hide the inline menu unless focus is still within self
    // consider lack of relatedTarget to still have focus to be on safe side - clickOutside gives user a way to close menu still
    const containsRelatedTarget = (event.relatedTarget == null) || this.getContainsEl(event.relatedTarget);
    if (!containsRelatedTarget) {
      this.onClickOutside();
    }
  }

  onPmUpdate() {
    this.calculateMenuPosition();
    this.setState({
      selStr: selToStr(this.pmSelection),
      hasContent: Pm.hasContent(this.pmView),
      menuState: Pm.getMenuState(this.pmView),
      hasFocus: this.pmView.hasFocus(),
    });
    this.props.onChange(this.md);
  }

  calculateMenuPosition() {
    if (!this.elMenuTarget) return;
    // dealing with screen coordinates
    const targetCoords = this.pmView.coordsAtPos(this.pmSelection.head);
    // calculate and set position
    const box = this.elMenuTarget.offsetParent.getBoundingClientRect();
    const left = targetCoords.left;
    this.elMenuTarget.style.left = (left - box.left) + 'px';
    this.elMenuTarget.style.bottom = (box.bottom - targetCoords.top) + 'px';
    // update popper
    Popper.updateAllNow();
  }

  getContainsEl(targetEl) {
    return this.ownEls.some(el => el.contains(targetEl));
  }

  get pmView() {
    return this._pmView;
  }
  get pmState() {
    return this.pmView?.state;
  }
  get pmDispatch() {
    return this.pmView?.dispatch;
  }
  get pmSelection() {
    return this.pmState?.selection;
  }
  get pmCmdArgs() {
    return [this.pmState, this.pmDispatch, this.pmView];
  }

  get elEditable() {
    if (!this.refEditorCon.current) return null;
    return this.refEditorCon.current.querySelector('[contenteditable].ProseMirror') || null;
  }
  get elMenuTarget() {
    if (!this.refPopper.current) return null;
    return this.refPopper.current.refTarget.current || null;
  }

  get md() {
    return Pm.docToMd(this.pmState.doc);
  }

  get linkHrefInputVal() {
    if (this.state.linkHref != null) return this.state.linkHref;
    return this.state.menuState.link.href || '';
  }

  get ownEls() {
    return [this.refInlineMenu.current, this.refMain.current, ...this.refMenu.current.els].filter(el => el);
  }

  get menu() {
    const {menuState} = this.state;
    const {hideTextBlocks} = this.props;

    // link item
    const {link} = menuState;
    const hasLink = link.active || link.partiallyActive;
    const linkChildren = [];
    if (link.active) {
      linkChildren.push({onClick: this.onClickLink, label: 'Edit URL', icon: Icon.Pencil});
    }
    if (hasLink) {
      linkChildren.push({onClick: this.onClickLinkRemove, label: 'Remove link', icon: Icon.Remove});
    }
    const linkItem = {
      key: 'link',
      title: 'Link',
      icon: Icon.HyperLink2,
      active: hasLink,
      disabled: !link.enabled,
      onClick: hasLink ? undefined : this.onClickLink,
      children: linkChildren,
    };

    // size item
    const sizeItem = {
      key: 'size',
      title: 'Size',
      label: textTypeAbbrMap[menuState.textType] || '-',
      icon: Icon.TextStyle,
      active: !!menuState.textType && (menuState.textType !== 'paragraph'),
      className: `text-type ${menuState.textType || ''}`,
      children: [
        {label: 'Heading 1',  className: 'wys-sample-h1',   onClick: this.onClickTextTypeH1},
        {label: 'Heading 2',  className: 'wys-sample-h2',   onClick: this.onClickTextTypeH2},
        {label: 'Heading 3',  className: 'wys-sample-h3',   onClick: this.onClickTextTypeH3},
        {label: 'Heading 4',  className: 'wys-sample-h4',   onClick: this.onClickTextTypeH4},
        {label: 'Text',       className: 'wys-sample-text', onClick: this.onClickTextTypeText},
        {label: 'Code Block', className: 'wys-sample-code', onClick: this.onClickTextTypeCode},
      ]
    };

    // format item
    const getMarkAttrs = (mark) => {
      return {disabled: !mark.enabled, active: (mark.active || mark.partiallyActive)};
    };
    // const markStates = [menuState.strong, menuState.em, menuState.code];
    // const formatItem = {
    //   key: 'format',
    //   title: 'Format',
    //   icon: Icon.TextOptions,
    //   active: markStates.some(ms => (ms.active || ms.partiallyActive)),
    //   disabled: !markStates.some(ms => ms.enabled),
    //   children: [
    //     {label: 'Bold',   icon: Icon.TextBold,      className: 'wys-sample-strong', onClick: this.onClickMarkStrong, ...getMarkAttrs(menuState.strong)},
    //     {label: 'Italic', icon: Icon.TextItalic,    className: 'wys-sample-em',     onClick: this.onClickMarkEm,     ...getMarkAttrs(menuState.em)},
    //     {label: 'Code',   icon: Icon.AngleBrackets, className: 'wys-sample-code',   onClick: this.onClickMarkCode,   ...getMarkAttrs(menuState.code)},
    //   ],
    // };
    const formatItems = [
      {key: 'mark-strong', title: 'Bold',   icon: Icon.TextBold,      onClick: this.onClickMarkStrong, ...getMarkAttrs(menuState.strong)},
      {key: 'mark-em',     title: 'Italic', icon: Icon.TextItalic,    onClick: this.onClickMarkEm,     ...getMarkAttrs(menuState.em)},
      {key: 'mark-code',   title: 'Code',   icon: Icon.AngleBrackets, onClick: this.onClickMarkCode,   ...getMarkAttrs(menuState.code)},
    ];

    const emojiItem = {
      key: 'emoji',
      title: 'Emoji',
      icon: Icon.MartialArtsKarate,
      active: false,
      disabled: false,
      onClick: this.onClickEmojis,
    };

    // lists, indentation, horizontal rule, new line
    const moreItem = {
      key: 'more',
      title: 'More',
      icon: Icon.DesignToolsPenStation,
      active: false,
      disabled: false,
      children: [
        {label: 'Lists'},
        {label: 'Bulleted List', icon: Icon.ListBullets,    onClick: this.onClickListifyBullet, disabled: !menuState.lists.canListifyBullet},
        {label: 'Numbered List', icon: Icon.ListNumbers,    onClick: this.onClickListifyNumber, disabled: !menuState.lists.canListifyNumber},
        {label: 'Nest',          icon: Icon.IndentIncrease, onClick: this.onClickListSink,      disabled: !menuState.lists.canSink},
        {label: 'Unnest',        icon: Icon.IndentDecrease, onClick: this.onClickListLift,      disabled: !menuState.lists.canLift},

        {label: 'Indentation'},
        {label: 'Indent',  icon: Icon.IndentIncrease, onClick: this.onClickQuote, disabled: !menuState.actions.canQuote},
        {label: 'Outdent', icon: Icon.IndentDecrease, onClick: this.onClickLift,  disabled: !menuState.actions.canLift},

        {label: 'Insert'},
        {label: 'New Line', onClick: this.onClickInsertNewLine},
        {label: 'Horizontal Rule', onClick: this.onClickInsertHr},
      ],
    };

    return [
      ...(hideTextBlocks ? [] : [sizeItem]),
      ...formatItems,
      linkItem, emojiItem, moreItem,
    ];
  }

  focus() {
    this.elEditable.focus({preventScroll: true});
  }

  insert(node) {
    const head = this.pmState.selection.head;
    const transaction = this.pmState.tr;
    transaction.insert(head, node);
    this.pmDispatch(transaction);
  }

  // link href
  onClickLink() {
    let inlineMenu;
    this.setState((prevState) => {
      inlineMenu = (prevState.inlineMenu === 'link') ? null : 'link';
      return {inlineMenu, linkHref: null};
    }, () => {
      Popper.updateAllNow();
      if (inlineMenu) {
        this.refHrefInput.current?.element?.select();
      }
    });
  }
  onChangeLinkHref(event) {
    const linkHref = event.target.value;
    this.setState({linkHref});
  }
  onKeyPressLinkHref(event) {
    const isEnter = event.key === 'Enter';
    if (!isEnter) return;
    this.setLinkHrefFromInput();
  }
  setLinkHrefFromInput() {
    const href = this.linkHrefInputVal;
    if (!utils.isUrl(href)) return;
    const linkState = this.state.menuState.link;
    const isCreating = !linkState.active && !linkState.partiallyActive;
    this.setState({linkHref: null, inlineMenu: null}, () => {
      this.focus();
      if (isCreating) {
        Pm.toggleMark(Pm.markLink, {href})(...this.pmCmdArgs);
      } else {
        this.updateLinkHref(href);
      }
    });
  }
  updateLinkHref(href) {
    const {from, to, mark} = Pm.getSelectionMarkInfo(this.pmState, Pm.markLink) || {};
    if (!mark) return;
    const newMark = Pm.markLink.create({href});
    const transaction = this.pmView.state.tr;
    transaction.removeMark(from, to, mark);
    transaction.addMark(from, to, newMark);
    this.pmDispatch(transaction);
  }
  onClickLinkAccept() {
    this.setLinkHrefFromInput();
  }
  onClickLinkRemove() {
    const linkState = this.state.menuState.link;
    const isCreating = !linkState.active && !linkState.partiallyActive;
    if (isCreating) {
      this.setState({inlineMenu: null});
      return;
    }
    const hasRange = this.pmSelection.from !== this.pmSelection.to;
    if (hasRange) {
      Pm.toggleMark(Pm.markLink)(...this.pmCmdArgs);
      return;
    }
    const {from, to, mark} = Pm.getSelectionMarkInfo(this.pmState, Pm.markLink) || {};
    if (!mark) return;
    const transaction = this.pmView.state.tr;
    transaction.removeMark(from, to, mark);
    this.pmDispatch(transaction);
  }

  // menu buttons
  preventDefault(event) {
    event.preventDefault();
  }
  onClickListifyBullet(event) {
    Pm.wrapInList(Pm.schema.nodes.bullet_list, {tight: true})(...this.pmCmdArgs);
  }
  onClickListifyNumber(event) {
    Pm.wrapInList(Pm.schema.nodes.ordered_list, {tight: true})(...this.pmCmdArgs);
  }
  onClickListSink(event) {
    Pm.sinkListItem(Pm.schema.nodes.list_item)(...this.pmCmdArgs);
  }
  onClickListLift(event) {
    Pm.liftListItem(Pm.schema.nodes.list_item)(...this.pmCmdArgs);
  }
  onClickQuote(event) {
    Pm.wrapIn(Pm.schema.nodes.blockquote)(...this.pmCmdArgs);
  }
  onClickLift(event) {
    Pm.lift(...this.pmCmdArgs);
  }
  onClickInsertHr(event) {
    this.insert(Pm.schema.nodes.horizontal_rule.create());
  }
  onClickInsertNewLine(event) {
    this.insert(Pm.schema.nodes.hard_break.create());
  }

  onClickEmojis() {
    this.setState((prevState) => {
      const newVal = (prevState.inlineMenu === 'emojis') ? null : 'emojis';
      return {inlineMenu: newVal};
    }, () => {
      Popper.updateAllNow();
    });
  }
  onSelectEmoji(emojiChar) {
    this.setState({inlineMenu: null});
    const head = this.pmState.selection.head;
    const transaction = this.pmState.tr;
    transaction.insertText(emojiChar, head, head);
    this.pmDispatch(transaction);
    this.focus();
  }

  onSelectTextType(value) {
    if (!value) return;
    const isHeading = value.startsWith('heading');
    const level = isHeading ? parseInt(value[7]) : undefined;
    const cmd = isHeading ? Pm.setBlockType(Pm.schema.nodes.heading, {level}) : Pm.setBlockType(Pm.schema.nodes[value]);
    cmd(...this.pmCmdArgs);
    this.focus();
  }
  onClickTextTypeText() { this.onSelectTextType('paragraph'); }
  onClickTextTypeH1()   { this.onSelectTextType('heading1'); }
  onClickTextTypeH2()   { this.onSelectTextType('heading2'); }
  onClickTextTypeH3()   { this.onSelectTextType('heading3'); }
  onClickTextTypeH4()   { this.onSelectTextType('heading4'); }
  onClickTextTypeCode() { this.onSelectTextType('code_block'); }

  onClickMark(markName) {
    const mark = Pm.schema.marks[markName];
    // const isLink = markName === 'link';
    // const alreadyHasLink = this.state.menuState.link.partiallyActive || this.state.menuState.link.active;
    // if (isLink && !alreadyHasLink) {
    //   this.setState((prevState) => ({isCreatingLink: !prevState.isCreatingLink}), () => {
    //     // TODO: focus href input
    //   });
    //   return;
    // }
    const cmd = Pm.toggleMark(mark);
    cmd(...this.pmCmdArgs);
  }
  onClickMarkStrong() { this.onClickMark('strong'); }
  onClickMarkEm()     { this.onClickMark('em'); }
  onClickMarkCode()   { this.onClickMark('code'); }

  renderInlineMenuLink() {
    const isUrl = utils.isUrl(this.linkHrefInputVal);
    return (
      <div className="rte-wys-imenu">
        <div className="rte-wys-imenu-row">
          <StandardInput value={this.linkHrefInputVal} onChange={this.onChangeLinkHref} onKeyPress={this.onKeyPressLinkHref} label="https://example.com" name="href" className="rte-wys-imenu-href" ref={this.refHrefInput} icon={<Icon.Network0 />} />
          <button title="Set Link" className={`rte-wys-imenu-btn href-accept`} disabled={!isUrl} onClick={this.onClickLinkAccept} onMouseDown={this.preventDefault}><Icon.Check1 /></button>
          <button title="Remove Link" className={`rte-wys-imenu-btn href-remove`} onClick={this.onClickLinkRemove} onMouseDown={this.preventDefault}><Icon.Remove /></button>
        </div>
      </div>
    );
  }
  renderInlineMenuEmojis() {
    return <EmojiPicker onSelect={this.onSelectEmoji} ref={this.refEmojiPicker} />;
  }
  renderInlineMenu() {
    const renderFn = ({
      link: this.renderInlineMenuLink,
      emojis: this.renderInlineMenuEmojis,
    })[this.state.inlineMenu];
    if (renderFn) return (
      <div ref={this.refInlineMenu}>{renderFn()}</div>
    );
    return '';
  }

  render() {
    const {hasContent, inlineMenu, hasFocus} = this.state;
    const {placeholder, tall} = this.props;
    const tallClass = tall ? 'tall' : '';
    const focusClass = (hasFocus || inlineMenu) ? 'has-focus' : 'no-focus';

    return (
      <div className={`rte-wys ${tallClass} ${focusClass}`} ref={this.refMain}>
        <Menu menu={this.menu} ref={this.refMenu} />
        <Popper
          tagType="div"
          tabIndex="-1"
          className="rte-wys-tooltip-target"
          ref={this.refPopper}
          popType="popover"
          popContent={this.renderInlineMenu()}
          popClassName="rte-wys-imenu-popover"
          placement="top"
          isActive={!!inlineMenu}
          usePortal
          edgeDistance={24}
        />
        <div
          className="rte-wys-editor-con"
          data-empty={`${!hasContent}`}
          data-placeholder={placeholder}
          ref={this.refEditorCon}
        />
      </div>
    );
  }

}

RteWysiwyg.propTypes = {
  initialMd: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  hideTextBlocks: PropTypes.bool,
  tall: PropTypes.bool,
};

RteWysiwyg.defaultProps = {
  initialMd: '',
  onChange: noop,
  placeholder: 'Type here...',
  hideTextBlocks: false,
  tall: false,
};

export default RteWysiwyg;
